import { defineStore } from 'pinia';
import employeeApi from "@/api/employeeApi.js";

export const useEmployeeStore = defineStore('employeeStore', {
    state: () => ({
        employees: [],
        allEmployees: [],
        employee: null,
        currentUser: null, // Данные текущего пользователя из /me/
        events: {
            count: 0,
            next: null,
            previous: null,
            results: [],
            page: 1,
        },
        allDepartments: [],
        allPositions: [],
    }),
    getters: {
        isAuthenticated: (state) => !!state.currentUser?.id,

        // ID роли (должности) текущего пользователя
        userPostId: (state) => state.currentUser?.post_id || null,

        userPermissions: (state) => {
            // Суперпользователь имеет все права
            if (state.currentUser?.is_superuser === true) {
                if (import.meta.env.DEV) console.log('[employeeStore.userPermissions] User is superuser, returning all permissions');
                return ['*']; // Специальный маркер для суперпользователя
            }

            // Backend возвращает права в поле effective_permissions, а не permissions
            const perms = state.currentUser?.effective_permissions || state.currentUser?.permissions;
            if (import.meta.env.DEV) console.log('[employeeStore.userPermissions] Raw permissions:', perms);
            if (import.meta.env.DEV) console.log('[employeeStore.userPermissions] Type:', typeof perms, 'IsArray:', Array.isArray(perms));

            // Если permissions это объект { "perm": true }, преобразуем в массив
            if (perms && typeof perms === 'object' && !Array.isArray(perms)) {
                const permArray = Object.keys(perms).filter(key => perms[key] === true);
                if (import.meta.env.DEV) console.log('[employeeStore.userPermissions] Converted object to array:', permArray.length, 'permissions');
                return permArray;
            }
            // Если permissions это массив или undefined, возвращаем как есть
            const result = Array.isArray(perms) ? perms : [];
            if (import.meta.env.DEV) console.log('[employeeStore.userPermissions] Returning array:', result.length, 'permissions');
            return result;
        },
        hasPermission: (state) => (perm) => {
            // Суперпользователь имеет все права
            if (state.currentUser?.is_superuser === true) {
                if (import.meta.env.DEV) console.log(`[employeeStore.hasPermission] User is superuser, granting "${perm}"`);
                return true;
            }

            // Backend возвращает права в поле effective_permissions, а не permissions
            const perms = state.currentUser?.effective_permissions || state.currentUser?.permissions;
            if (import.meta.env.DEV) console.log(`[employeeStore.hasPermission] Checking "${perm}"`, { perms, type: typeof perms });

            // Если permissions это объект { "perm": true }
            if (perms && typeof perms === 'object' && !Array.isArray(perms)) {
                const result = perms[perm] === true;
                if (import.meta.env.DEV) console.log(`[employeeStore.hasPermission] Object check for "${perm}":`, result);
                return result;
            }
            // Если permissions это массив
            const result = Array.isArray(perms) ? perms.includes(perm) : false;
            if (import.meta.env.DEV) console.log(`[employeeStore.hasPermission] Array check for "${perm}":`, result);
            return result;
        },
    },


    actions: {
        async loadEmployees({ page = 1, page_size, search = '', is_active } = {}) {
            try {
                const params = { page };
                if (page_size) params.page_size = page_size;
                if (search) params.q = search;
                if (typeof is_active === 'boolean') params.is_active = is_active;

                const responseData = await employeeApi.getEmployees(params);

                if (responseData && Array.isArray(responseData.results)) {
                    this.employees = responseData.results.map(it => ({
                        ...it,
                        full_name: it.full_name || [it.last_name, it.first_name].filter(Boolean).join(' '),
                    }));
                }
                return responseData;
            } catch (error) {
                console.error('Ошибка загрузки сотрудников:', error);
                throw error;
            }
        },

        async fetchAllEmployees() {
            if (this.allEmployees.length > 0) return;
            try {
                let page = 1;
                const all = [];
                const MAX_PAGES = 1000;
                for (let i = 0; i < MAX_PAGES; i++) {
                    const responseData = await employeeApi.getEmployees({ page });
                    if (responseData && Array.isArray(responseData.results)) {
                        all.push(...responseData.results);
                    }
                    if (!responseData?.next) break;
                    page += 1;
                }
                this.allEmployees = all;
            } catch (error) {
                console.error('Ошибка загрузки полного списка сотрудников:', error);
            }
        },

        async loadEmployee(id) {
            try {
                this.employee = await employeeApi.getEmployee(id);
            } catch (error) {
                console.error('Ошибка загрузки сотрудника:', error);
            }
        },

        async loadEvents({ target = 'user', id, page = 1 }) {
            try {
                const data = await employeeApi.getEvents({ target, id, page });
                this.events = {
                    count: data?.count ?? 0,
                    next: data?.next ?? null,
                    previous: data?.previous ?? null,
                    results: Array.isArray(data?.results) ? data.results : [],
                    page,
                };
            } catch (error) {
                console.error('Ошибка загрузки событий сотрудника:', error);
            }
        },

        async fetchAllDepartments() {
            if (this.allDepartments.length > 0) return;
            try {
                const data = await employeeApi.getDepartments();
                this.allDepartments = Array.isArray(data) ? data : [];
            } catch (error) {
                console.error('Ошибка загрузки всех департаментов:', error);
            }
        },

        async fetchAllPositions() {
            if (this.allPositions.length > 0) return;
            try {
                const data = await employeeApi.getPositions();
                this.allPositions = Array.isArray(data) ? data : [];
            } catch (error) {
                console.error('Ошибка загрузки всех должностей:', error);
            }
        },

        async addEmployee(formData) {
            try {
                await employeeApi.createEmployee(formData);
                await this.loadEmployees({});
            } catch (error) {
                console.error('Ошибка добавления сотрудника:', error);
                throw error;
            }
        },

        async updateEmployee(id, formData) {
            try {
                await employeeApi.updateEmployee(id, formData);
                await this.loadEmployee(id);
                await this.loadEmployees({});
            } catch (error) {
                console.error('Ошибка обновления сотрудника:', error);
                throw error;
            }
        },

        // Деактивация сотрудника
        async deactivateEmployee(id) {
            try {
                await employeeApi.deactivateEmployee(id);
                // Перезагрузим детали (статус is_active уже false) и список
                await this.loadEmployee(id);
                await this.loadEmployees({});
            } catch (error) {
                console.error('Ошибка деактивации сотрудника:', error);
                throw error;
            }
        },
        async unarchiveEmployee(id) {
            try {
                await employeeApi.unarchiveEmployee(id);
                await this.loadEmployee(id);
            } catch (error) {
                console.error('Ошибка разархивирования/активации сотрудника:', error);
                throw error;
            }
        },

        async fetchCurrentUser() {
            try {
                const me = await employeeApi.getCurrentUser();
                this.currentUser = me || null;

                // Очищаем кеш типов документов при смене пользователя
                // Импортируем здесь, чтобы избежать циклических зависимостей
                const { useAttachmentStore } = await import('@/stores/attachment.js');
                const attachmentStore = useAttachmentStore();
                attachmentStore.documentTypes = {};
                if (import.meta.env.DEV) console.log('[employeeStore] User changed, cleared document types cache');

                return this.currentUser;
            } catch (error) {
                console.error('Ошибка загрузки текущего пользователя:', error);
                this.currentUser = null;
                return null;
            }
        }

    },
});
